import Phaser from 'phaser';
import { StateMachine, asyncTween, frameMs } from 'tower/util';
import BattleScene from 'tower/scenes/battle';
import Enemy, { EnemyState } from 'tower/scenes/battle/enemy';

const VERTICAL_ATTACK_DISTANCE = 24 * 3 - 6;
const HORIZONTAL_ATTACK_DISTANCE = 24 * 5 - 6;

interface Variant {
  idle: number;
  touchDamage: number;
}

const VARIANTS: Record<string, Variant> = {
  blue: {
    idle: 6,
    touchDamage: 2,
  },
};

export default class BladeTrap extends Enemy {
  constructor(
    scene: BattleScene,
    public initialX: number,
    public initialY: number,
    public variantType = 'blue',
  ) {
    const variant = VARIANTS[variantType];
    super(scene, initialX, initialY, 'enemies', variant.idle, 'bladeTrap', {
      touchDamage: variant.touchDamage,
    });
    this.body.setSize(12, 12);
  }

  makeStateMachine(scene: BattleScene): StateMachine<EnemyState> {
    return new StateMachine('idle', {
      idle: new BladeTrapIdleState(scene, this),
      dash: new BladeTrapDashState(scene, this),
      return: new BladeTrapReturnState(scene, this),
    });
  }

  get variant() {
    return VARIANTS[this.variantType];
  }
}

class BladeTrapState extends EnemyState {
  declare enemy: BladeTrap;
}

class BladeTrapIdleState extends BladeTrapState {
  async handleEntered() {
    const { enemy } = this;

    enemy.stop().setFrame(enemy.variant.idle);
  }

  update() {
    const { scene, enemy } = this;
    const { player } = scene;

    // Vertical attack
    if (player.x >= enemy.x - enemy.body.halfWidth && player.x < enemy.x + enemy.body.halfWidth) {
      if (player.y < enemy.y) {
        return this.transition('dash', enemy.x, enemy.y - VERTICAL_ATTACK_DISTANCE);
      } else {
        return this.transition('dash', enemy.x, enemy.y + VERTICAL_ATTACK_DISTANCE);
      }
    }

    // Horizontal attack
    if (player.y >= enemy.y - enemy.body.halfHeight && player.y < enemy.y + enemy.body.halfHeight) {
      if (player.x < enemy.x) {
        return this.transition('dash', enemy.x - HORIZONTAL_ATTACK_DISTANCE, enemy.y);
      } else {
        return this.transition('dash', enemy.x + HORIZONTAL_ATTACK_DISTANCE, enemy.y);
      }
    }
  }
}

class BladeTrapDashState extends BladeTrapState {
  async handleEntered(targetX: number, targetY: number) {
    const { enemy, scene } = this;

    const distance = Phaser.Math.Distance.Between(enemy.x, enemy.y, targetX, targetY);

    await asyncTween(scene, {
      x: targetX,
      y: targetY,
      targets: [enemy],
      duration: (distance / 3) * frameMs(1),
    });

    return this.transition('return');
  }
}

class BladeTrapReturnState extends BladeTrapState {
  async handleEntered() {
    const { enemy, scene } = this;

    const distance = Phaser.Math.Distance.Between(enemy.x, enemy.y, enemy.initialX, enemy.initialY);

    await asyncTween(scene, {
      x: enemy.initialX,
      y: enemy.initialY,
      targets: [enemy],
      duration: distance * frameMs(2),
    });

    enemy.setPosition(enemy.initialX, enemy.initialY);
    return this.transition('idle');
  }
}
